// Maps a persisted Stripe tax-id value to the country-specific `TaxIdType` that
// Stripe's `TaxIdElement` accepts as a default. Supported types:
// https://docs.stripe.com/billing/customer/tax-ids#supported-tax-id
import type { TaxIdType } from '@stripe/stripe-js';

// VAT-number country prefixes for EU member states → the Stripe TaxIdElement's
// country-specific `TaxIdType`. We persist the aggregate `eu_vat` external type,
// so the specific member state is recovered from the VAT number's leading code.
// Greece uses the "EL" prefix and Northern Ireland uses "XI".
const EU_VAT_PREFIX_TO_TAX_ID_TYPE = {
  AT: 'at_vat',
  BE: 'be_vat',
  BG: 'bg_vat',
  CY: 'cy_vat',
  CZ: 'cz_vat',
  DE: 'de_vat',
  DK: 'dk_vat',
  EE: 'ee_vat',
  EL: 'gr_vat',
  ES: 'es_vat',
  FI: 'fi_vat',
  FR: 'fr_vat',
  HR: 'hr_vat',
  HU: 'hu_vat',
  IE: 'ie_vat',
  IT: 'it_vat',
  LT: 'lt_vat',
  LU: 'lu_vat',
  LV: 'lv_vat',
  MT: 'mt_vat',
  NL: 'nl_vat',
  PL: 'pl_vat',
  PT: 'pt_vat',
  RO: 'ro_vat',
  SE: 'se_vat',
  SI: 'si_vat',
  SK: 'sk_vat',
  XI: 'xi_vat',
} as const satisfies Record<string, TaxIdType>;

type EuVatPrefix = keyof typeof EU_VAT_PREFIX_TO_TAX_ID_TYPE;

function isEuVatPrefix(value: string): value is EuVatPrefix {
  return value in EU_VAT_PREFIX_TO_TAX_ID_TYPE;
}

// Every `externalTaxIdType` the TaxIdElement reports — except the aggregate
// `eu_vat` (handled above) — is itself a country-specific `TaxIdType` accepted
// by `defaultValues.taxIdType`. Listed for runtime validation so only
// element-recognized types are passed back; `satisfies` guards against typos.
const ELEMENT_TAX_ID_TYPES = [
  'ae_trn',
  'al_tin',
  'am_tin',
  'ao_tin',
  'au_abn',
  'aw_tin',
  'az_tin',
  'ba_tin',
  'bb_tin',
  'bd_bin',
  'bf_ifu',
  'bh_vat',
  'bj_ifu',
  'bs_tin',
  'by_tin',
  'ca_bn',
  'ca_gst_hst',
  'ca_pst_bc',
  'ca_pst_mb',
  'ca_pst_sk',
  'ca_qst',
  'cd_nif',
  'ch_vat',
  'cl_tin',
  'cm_niu',
  'cr_tin',
  'cv_nif',
  'ec_ruc',
  'eg_tin',
  'es_cif',
  'et_tin',
  'gb_vat',
  'ge_vat',
  'gn_nif',
  'in_gst',
  'is_vat',
  'ke_pin',
  'kg_tin',
  'kh_tin',
  'kr_brn',
  'kz_bin',
  'la_tin',
  'li_vat',
  'lk_vat',
  'ma_vat',
  'md_vat',
  'me_pib',
  'mk_vat',
  'mr_nif',
  'mx_rfc',
  'ng_tin',
  'no_vat',
  'np_pan',
  'nz_gst',
  'om_vat',
  'pe_ruc',
  'ph_tin',
  'rs_pib',
  'ru_inn',
  'ru_kpp',
  'sa_vat',
  'sg_gst',
  'sn_ninea',
  'sr_fin',
  'th_vat',
  'tj_tin',
  'tr_tin',
  'tw_vat',
  'tz_vat',
  'ua_vat',
  'ug_tin',
  'uy_ruc',
  'uz_tin',
  'uz_vat',
  'za_vat',
  'zm_tin',
  'zw_tin',
] as const satisfies readonly TaxIdType[];

const ELEMENT_TAX_ID_TYPE_SET: ReadonlySet<string> = new Set(
  ELEMENT_TAX_ID_TYPES
);

function isElementTaxIdType(value: string): value is TaxIdType {
  return ELEMENT_TAX_ID_TYPE_SET.has(value);
}

/**
 * Resolve the country-specific `TaxIdType` to pre-select in Stripe's
 * `TaxIdElement` from the persisted external `taxType` and tax id value.
 *
 * Returns `undefined` when the region can't be determined (an `eu_vat` with an
 * unrecognized prefix, or a type the element doesn't accept), so callers can
 * skip prefilling rather than render an invalid, "wrong region" selection.
 */
export function getStripeTaxIdElementType(
  taxType?: string | null,
  taxId?: string | null
): TaxIdType | undefined {
  if (!taxType) return undefined;

  if (taxType === 'eu_vat') {
    const prefix = taxId?.trim().slice(0, 2).toUpperCase();
    return prefix && isEuVatPrefix(prefix)
      ? EU_VAT_PREFIX_TO_TAX_ID_TYPE[prefix]
      : undefined;
  }

  return isElementTaxIdType(taxType) ? taxType : undefined;
}
